import { Box } from '@pco/atoms/Box';
import { Icon, type IconProps } from '@pco/atoms/Icon';
import { Typography } from '@pco/atoms/Typography';
import type { Vehicle } from '@pco/modules/VehicleSelection';
import type { Nullish } from '@pco/types';
import { getDataAttrs } from '@pco/utils/getDataAttrs';
import styled, { css, type DefaultTheme } from 'styled-components';

const dataAttrs = getDataAttrs(['molecule', 'fitment-message']);

export interface FitmentMessageProps
	extends React.HTMLAttributes<HTMLDivElement> {
	mainText: string;
	subText?: string;
	iconStart?: React.ReactNode;
	iconEnd?: React.ReactNode;
	as?: string;
	type?: string;
}

type FitmentIcons = {
	fallbackIcon: IconProps['fallbackIcon'];
	background?: string;
	color?: string;
	foreground?: string;
	src?: string;
};

type FitmentVariantConfig = {
	mainText: string;
	subText: string;
	iconStartProps?: FitmentIcons;
	iconEndProps?: FitmentIcons;
	asButton?: boolean;
};

const TruncatedTypography = styled(Typography)`
	overflow: hidden;
	// white-space: nowrap;
	text-overflow: ellipsis;

	${({ theme }) => `
	
	${theme.breakpoints('tablet', 'up')} {
		white-space: unset;
	}
  `}

`;

const NoWrapTypography = styled(Typography)`
	white-space: nowrap;
`;

const FitmentIcon = styled(Icon)<FitmentIcons>`
  ${({
		theme,
		background,
		foreground,
		color,
	}: { theme: DefaultTheme } & FitmentIcons) => {
		const colors = theme.semanticTokens.colors;

		if (background || foreground) {
			return css`
        & .icon-background {
          fill: ${background ? resolveThemeColor(theme, background) : colors.content.primary};
        }
        & .icon-foreground {
          fill: ${foreground ? resolveThemeColor(theme, foreground) : colors.content.primary};
        }
      `;
		}

		return css`
        color: ${resolveThemeColor(theme, color) ?? colors.content.primary};
      `;
	}}
`;

const transformText = (
	subText: string,
	vehicle?: Nullish<Vehicle>,
	vehicleName?: string,
) => {
	return vehicleName
		? subText.replace('{year}', vehicleName).replace('{model}', '')
		: subText
				.replace('{year}', vehicle?.year ?? '')
				.replace('{model}', vehicle?.model ?? '');
};

function FitmentMessageMain({
	iconStart,
	mainText,
	subText,
	iconEnd,
	type,
	...rest
}: FitmentMessageProps) {
	return (
		<Box
			gap="4px"
			alignItems="center"
			width="100%"
			backgroundColor="transparent"
			border="none"
			cursor={type === 'button' ? 'pointer' : 'default'}
			type={type}
			{...dataAttrs('root')()}
			{...rest}
		>
			{iconStart != null && iconStart}
			<NoWrapTypography $weight="semibold" _size="body12">
				{mainText}
			</NoWrapTypography>
			{subText != null && (
				<TruncatedTypography
					title={`${mainText} ${subText}`}
					_size="body12"
					$lineClamp={1}
				>
					{subText}
				</TruncatedTypography>
			)}
			{iconEnd != null && iconEnd}
		</Box>
	);
}

export type FitmentMessageVariantProps = Partial<
	Omit<FitmentMessageProps, 'iconStart' | 'iconEnd'>
> & {
	iconStartProps?: Nullish<IconProps>;
	iconEndProps?: Nullish<IconProps>;
};

function resolveThemeColor(
	theme: DefaultTheme,
	token?: string,
): string | undefined {
	if (!token) return undefined;
	return token
		.split('.')
		.reduce<unknown>(
			(acc, key) =>
				acc && typeof acc === 'object' && key in acc
					? (acc as Record<string, unknown>)[key]
					: undefined,
			theme.semanticTokens.colors as Record<string, unknown>,
		) as string | undefined;
}

function createFitmentVariant({
	mainText,
	subText,
	iconStartProps,
	iconEndProps,
	asButton = false,
}: FitmentVariantConfig) {
	return function FitmentVariant({
		vehicle,
		vehicleName,
		mainText: overrideMainText = mainText,
		subText: overrideSubText = subText,
		...rest
	}: FitmentMessageVariantProps & {
		vehicle?: Nullish<Vehicle>;
		vehicleName?: string;
	}) {
		const resolvedSubText = transformText(
			overrideSubText,
			vehicle,
			vehicleName,
		);
		const accessibleText = `${overrideMainText} ${resolvedSubText}`;
		return (
			<FitmentMessage
				as={asButton ? 'button' : undefined}
				type={asButton ? 'button' : undefined}
				mainText={overrideMainText}
				subText={resolvedSubText}
				iconStart={
					iconStartProps?.fallbackIcon && (
						<FitmentIcon
							{...iconStartProps}
							aria-label={accessibleText}
							title={accessibleText}
						/>
					)
				}
				iconEnd={
					iconEndProps?.fallbackIcon && (
						<FitmentIcon
							aria-label={accessibleText}
							fallbackIcon={iconEndProps.fallbackIcon}
							title={accessibleText}
						/>
					)
				}
				{...rest}
			/>
		);
	};
}

const ExactFit = createFitmentVariant({
	mainText: 'Exact fit',
	subText: 'for your {model} {year}',
	iconStartProps: {
		fallbackIcon: 'circleCheckmark',
		background: 'background.inverse',
		foreground: 'content.primaryInverse',
		src: undefined,
	},
});

const CompatibleWithMost = createFitmentVariant({
	mainText: 'Compatible with most',
	subText: 'trims of your {model} {year}',
	iconStartProps: {
		fallbackIcon: 'circleCheckmark',
		background: 'background.accent.blueDark',
		foreground: 'content.primary',
	},
});

const CheckToSeeIfItFits = createFitmentVariant({
	mainText: 'Check',
	subText: 'to see if it fits',
	iconStartProps: {
		fallbackIcon: 'alert',
		color: 'background.validation.warning',
	},
	iconEndProps: { fallbackIcon: 'chevronRight' },
	asButton: true,
});

const MayNotFit = createFitmentVariant({
	mainText: 'May not fit',
	subText: 'your {model} {year}',
	iconStartProps: { fallbackIcon: 'alert', color: 'content.validation.error' },
});

const NotAvailable = createFitmentVariant({
	mainText: 'Fitment not available',
	subText: '',
	iconStartProps: { fallbackIcon: 'help', color: 'content.state.disabledDark' },
});

export const FitmentMessage = Object.assign(FitmentMessageMain, {
	ExactFit,
	CompatibleWithMost,
	CheckToSeeIfItFits,
	MayNotFit,
	NotAvailable,
});
